<template>
  <div class="progress-tab">
    <v-alert v-if="!members.length" type="info" text class="mb-0">
      {{ $t("progressTab.empty") }}
    </v-alert>

    <template v-else>
      <v-select
        v-model="selectedStudentId"
        :items="studentOptions"
        item-text="text"
        item-value="value"
        :label="$t('progressTab.student')"
        outlined
        dense
        hide-details="auto"
        class="progress-tab__select"
        @change="loadProgress"
      />

      <div v-if="loading" class="progress-tab__hint mt-5">
        {{ $t("progressTab.loading") }}
      </div>

      <template v-else>
        <div class="progress-tab__summary mt-5">
          <div class="progress-tab__count">
            {{
              $t("progressTab.completed", {
                done: progress.completedCount,
                total: progress.totalLessons,
              })
            }}
          </div>
          <v-chip small dark :color="theme.primary">
            {{ formatPercentage(progress.completedPercentage) }}
          </v-chip>
        </div>

        <v-progress-linear
          :value="progress.completedPercentage"
          rounded
          height="8"
          class="mt-3"
          color="#00A379"
          background-color="rgba(148, 163, 184, 0.25)"
        />

        <div class="progress-tab__hint mt-3">
          {{ $t("progressTab.hint") }}
        </div>

        <div v-if="checklist.length" class="progress-tab__list mt-4">
          <label
            v-for="subject in checklist"
            :key="subject.id"
            class="progress-item"
            :class="{ 'progress-item--done': subject.completed }"
          >
            <v-checkbox
              :input-value="subject.completed"
              :loading="togglingId === subject.id"
              :disabled="togglingId === subject.id"
              color="#00A379"
              hide-details
              dense
              class="ma-0 pa-0 progress-item__check"
              @change="toggleSubject(subject)"
            />
            <span class="progress-item__title">{{ subject.title }}</span>
          </label>
        </div>

        <v-alert v-else type="info" text class="mt-4 mb-0">
          {{ $t("progressTab.noSubjects") }}
        </v-alert>
      </template>
    </template>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";

const emptyProgress = () => ({
  totalLessons: 0,
  completedCount: 0,
  remainingCount: 0,
  completedPercentage: 0,
  completedClasses: [],
  remainingClasses: [],
  classes: [],
});

export default {
  name: "ClassProgressTab",

  props: {
    groupId: {
      type: [String, Number],
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      togglingId: null,
      selectedStudentId: null,
      progress: emptyProgress(),
    };
  },

  computed: {
    studentOptions() {
      return this.members.map((member) => ({
        text: member.name || this.$t("common.student"),
        value: member.id,
      }));
    },
    checklist() {
      if (
        Array.isArray(this.progress.classes) &&
        this.progress.classes.length
      ) {
        return this.progress.classes;
      }

      // fallback: monta a partir das listas separadas (retrocompatível)
      const completed = (this.progress.completedClasses || []).map((item) => ({
        ...item,
        completed: true,
      }));
      const remaining = (this.progress.remainingClasses || []).map((item) => ({
        ...item,
        completed: false,
      }));

      return [...completed, ...remaining];
    },
  },

  watch: {
    members: {
      immediate: true,
      handler(members) {
        const exists = (members || []).some(
          (member) => member.id === this.selectedStudentId
        );

        if (!exists) {
          this.selectedStudentId =
            members && members.length ? members[0].id : null;
        }

        if (this.selectedStudentId) {
          this.loadProgress();
        }
      },
    },
  },

  methods: {
    async loadProgress() {
      if (!this.selectedStudentId) {
        this.progress = emptyProgress();
        return;
      }

      const requestedId = this.selectedStudentId;
      this.loading = true;

      try {
        const res = await partnerApi.getStudentSubjectSequence(
          this.groupId,
          requestedId
        );

        if (this.selectedStudentId !== requestedId) {
          return;
        }

        this.applyProgress(res.data);
      } catch (error) {
        this.progress = emptyProgress();
      } finally {
        this.loading = false;
      }
    },
    applyProgress(data) {
      const payload = data || {};
      this.progress = {
        totalLessons: payload.totalLessons || 0,
        completedCount: payload.completedCount || 0,
        remainingCount: payload.remainingCount || 0,
        completedPercentage: payload.completedPercentage || 0,
        completedClasses: Array.isArray(payload.completedClasses)
          ? payload.completedClasses
          : [],
        remainingClasses: Array.isArray(payload.remainingClasses)
          ? payload.remainingClasses
          : [],
        classes: Array.isArray(payload.classes) ? payload.classes : [],
      };
    },
    async toggleSubject(subject) {
      if (!this.selectedStudentId || this.togglingId) {
        return;
      }

      const nextCompleted = !subject.completed;
      this.togglingId = subject.id;

      try {
        const res = await partnerApi.setStudentSubjectProgress(
          this.groupId,
          this.selectedStudentId,
          { courseClassId: subject.id, completed: nextCompleted }
        );

        this.applyProgress(res.data);

        this.$root.$emit("display-alert", {
          text: nextCompleted
            ? this.$t("progressTab.marked")
            : this.$t("progressTab.unmarked"),
          type: 0,
        });
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            this.$t("progressTab.updateFailed"),
          type: 1,
        });
      } finally {
        this.togglingId = null;
      }
    },
    formatPercentage(value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
.progress-tab__select {
  max-width: 360px;
}

.progress-tab__summary {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-tab__count {
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

.progress-tab__hint {
  color: var(--dk-muted, #6b6472);
  font-size: 0.92rem;
}

.progress-tab__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  border-radius: 12px;
  background: var(--dk-surface, #ffffff);
  cursor: pointer;
}

.progress-item--done {
  background: rgba(0, 163, 121, 0.07);
  border-color: rgba(0, 163, 121, 0.3);
}

.progress-item__check {
  flex: 0 0 auto;
}

.progress-item__title {
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
}

.progress-item--done .progress-item__title {
  color: var(--dk-teal-deep, #007f5e);
}
</style>
